import React, { useState } from 'react';

import { useVaultQuery } from '#/api/queries/vaultsQueries';
import { useOwnVaultQuery } from '#/components/vaults/shared/utils';
import { vaultEndStateCopy } from '#/components/vaults/shared/vaultEndStateCopy';
import { formatDateTime } from '#/lib/date';

import Banner from './Banner';

type EndState = 'liquidated' | 'exited';

/** Session-only dismissal (survives remounts, resets on page reload) */
let dismissedInSession: Record<EndState, boolean> = {
  liquidated: false,
  exited: false,
};

const noticeByEndState = {
  liquidated: {
    label: vaultEndStateCopy.liquidated.label,
    message: vaultEndStateCopy.liquidated.message,
    type: 'error',
  },
  exited: {
    label: vaultEndStateCopy.exited.label,
    message: vaultEndStateCopy.exited.message,
    type: 'warn',
  },
} as const;

function BannerNoticeVaultEndStateContent({
  managerWallet,
}: {
  managerWallet: string;
}) {
  const { data: vaultData } = useVaultQuery({ managerWallet });
  const [dismissedStates, setDismissedStates] = useState(dismissedInSession);

  const endState: EndState | undefined = vaultData?.liquidatedTime
    ? 'liquidated'
    : vaultData?.exitedTime
    ? 'exited'
    : undefined;
  const endStateTime =
    endState === 'liquidated'
      ? vaultData?.liquidatedTime
      : vaultData?.exitedTime;

  if (!endState || dismissedStates[endState]) {
    return null;
  }

  const notice = noticeByEndState[endState];

  return (
    <Banner
      notice={{
        type: notice.type,
        message: notice.message,
        isDismissable: true,
      }}
      onClose={() => {
        dismissedInSession = { ...dismissedInSession, [endState]: true };
        setDismissedStates(dismissedInSession);
      }}
    >
      <strong>{notice.label}</strong>{' '}
      {endStateTime ? `${formatDateTime(endStateTime)} ` : null}
    </Banner>
  );
}

/** Banner shown to a vault manager when their own vault was liquidated or exited. */
export default function BannerNoticeVaultEndState() {
  const ownVault = useOwnVaultQuery();

  if (!ownVault) {
    return null;
  }

  return <BannerNoticeVaultEndStateContent managerWallet={ownVault.manager} />;
}
